import { useState } from 'react';
import { supabase } from '../lib/supabase';
import calendlyService from '../services/calendlyService';

export const useCalendlyBooking = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Book a coaching session (Calendly appointment) with user data prefill
  const bookSession = async ({
    sessionDate,
    sessionType = 'consultation',
    title = 'Consulta de Evaluación Gratuita',
    description,
    userEmail,
    userName,
    userPhone = null,
    calendlyEventId = null,
    userData = {} // Quiz results and additional user data for prefill
  }) => {
    setIsLoading(true);
    setError(null);

    try {
      // First, check if user profile exists or create one
      let userId = null;
      
      if (userEmail) {
        // Try to find existing user by email
        const { data: existingUser } = await supabase?.from('user_profiles')?.select('id')?.eq('email', userEmail)?.single();

        if (existingUser) {
          userId = existingUser?.id;
        } else {
          // Create anonymous user profile for booking
          const { data: newUser, error: userError } = await supabase?.from('user_profiles')?.insert({
              email: userEmail,
              full_name: userName || 'Usuario Anónimo',
              phone: userPhone,
              user_type: 'anonymous'
            })?.select('id')?.single();

          if (userError) throw userError;
          userId = newUser?.id;
        }
      }

      // Get the coach ID (Miri Espejo)
      const { data: coachData } = await supabase?.from('user_profiles')?.select('id')?.eq('role', 'coach')?.single();

      if (!coachData) {
        throw new Error('Coach not found');
      }

      // Create coaching session record
      const sessionData = {
        client_id: userId,
        coach_id: coachData?.id,
        session_date: sessionDate,
        session_type: sessionType,
        title: title,
        description: description || 'Consulta de evaluación personalizada gratuita',
        status: 'scheduled',
        duration_minutes: 30, // Updated to 30 minutes for the specific URL
        notes: calendlyEventId ? `Calendly Event ID: ${calendlyEventId}` : null
      };

      const { data: session, error: sessionError } = await supabase?.from('coaching_sessions')?.insert(sessionData)?.select('*')?.single();

      if (sessionError) throw sessionError;

      // Generate Calendly URL with user data prefill
      const calendlyUrl = calendlyService?.generateCalendlyUrl({
        name: userName,
        email: userEmail,
        phone: userPhone,
        ...userData // Include quiz results and other data
      }, '30min');

      // Track the booking event for analytics
      if (typeof window !== 'undefined' && window?.gtag) {
        window.gtag('event', 'calendly_booking_initiated', {
          event_category: 'calendly',
          event_label: sessionType,
          value: 1,
          biological_age: userData?.biologicalAge,
          lead_temperature: userData?.leadTemperature
        });
      }

      return { 
        session, 
        userId, 
        calendlyUrl,
        prefillData: userData
      };

    } catch (err) {
      console.error('Error booking session:', err);
      setError(err?.message || 'Error al agendar la consulta');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Open Calendly widget with prefilled user data
  const openCalendlyWidget = (userData = {}, meetingType = '30min') => {
    try {
      calendlyService?.openCalendlyWidget(userData, meetingType);
    } catch (err) {
      console.error('Error opening Calendly widget:', err);
      setError('Error al abrir el calendario de citas');
    }
  };

  // Generate direct Calendly URL for external use
  const getCalendlyUrl = (userData = {}, meetingType = '30min') => {
    return calendlyService?.generateCalendlyUrl(userData, meetingType);
  };

  // Get user's booked sessions
  const getUserSessions = async (userId) => {
    try {
      const { data, error } = await supabase?.from('coaching_sessions')?.select(`
          *,
          coach:user_profiles!coaching_sessions_coach_id_fkey(
            full_name,
            email
          )
        `)?.eq('client_id', userId)?.order('session_date', { ascending: true });

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error fetching user sessions:', err);
      setError(err?.message);
      return [];
    }
  };

  // Update session status (for cancellations, completions, etc.)
  const updateSessionStatus = async (sessionId, status, notes = null) => {
    setIsLoading(true);
    setError(null);

    try {
      const updateData = { status, updated_at: new Date()?.toISOString() };
      if (notes) updateData.notes = notes;

      const { data, error } = await supabase?.from('coaching_sessions')?.update(updateData)?.eq('id', sessionId)?.select('*')?.single();

      if (error) throw error;

      // Track cancellation if applicable
      if (status === 'cancelled' && typeof window !== 'undefined' && window?.gtag) {
        window.gtag('event', 'calendly_booking_cancelled', {
          event_category: 'calendly',
          event_label: 'user_cancellation',
          value: 1
        });
      }

      return data;
    } catch (err) {
      console.error('Error updating session:', err);
      setError(err?.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Get available time slots (mock data - in production, this would integrate with actual Calendly API)
  const getAvailableSlots = async (startDate, endDate) => {
    // This is mock data - in a real implementation, you'd call Calendly API
    const mockSlots = [
      { date: '2025-01-08', time: '10:00', available: true },
      { date: '2025-01-08', time: '14:00', available: true },
      { date: '2025-01-08', time: '16:00', available: false },
      { date: '2025-01-09', time: '09:00', available: true },
      { date: '2025-01-09', time: '11:00', available: true },
      { date: '2025-01-09', time: '15:00', available: true },
      { date: '2025-01-10', time: '10:00', available: true },
      { date: '2025-01-10', time: '13:00', available: false }
    ];

    // Filter by date range if provided
    if (startDate || endDate) {
      return mockSlots?.filter(slot => {
        const slotDate = new Date(slot?.date);
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;
        
        return (!start || slotDate >= start) && (!end || slotDate <= end);
      });
    }

    return mockSlots;
  };

  return {
    bookSession,
    openCalendlyWidget,
    getCalendlyUrl,
    getUserSessions,
    updateSessionStatus,
    getAvailableSlots,
    isLoading,
    error,
    setError
  };
};